import React, { useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import { getCompanyById } from '../../slices/companies/thunk';
import { jwtDecode } from 'jwt-decode';
import RestrictionMessage from '../../Components/RestrictionMessage';

// Helper para decodificar el token JWT
const decodeJwtPayload = (token: string) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

// Función para validar si la información de la compañía está completa
const isCompanyInformationComplete = (company: any) => {
    if (!company) return false;

    const isDefaultInfo =
        company.address === 'Dirección por defecto - Actualizar después' &&
        company.ruc === '9999999999999' &&
        (!company.phone || company.phone === '');

    if (isDefaultInfo) return false;

    return !!(
        company.businessName &&
        company.ruc &&
        company.address &&
        company.address !== 'Dirección por defecto - Actualizar después' &&
        company.ruc !== '9999999999999' &&
        company.ruc.length === 13
    );
};

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();
    const { selectedCompany, isLoading: isLoadingCompanies } = useSelector((state: any) => state.Companies);

    // Obtener información del usuario y token
    const authUser = localStorage.getItem("authUser");
    const userToken = authUser ? JSON.parse(authUser).token : null;
    const decodedToken = userToken ? decodeJwtPayload(userToken) : null;
    const userRoles = decodedToken?.roles?.map((role: any) => role.name) || [];
    const isSuperAdmin = userRoles.includes('Super Admin');
    const isAdmin = userRoles.includes('Admin') || isSuperAdmin;
    const isUser = userRoles.includes('User') && !isAdmin;
    const companyId = decodedToken?.companyId;

    useEffect(() => {
        if (authUser) {
            try {
                const { token } = JSON.parse(authUser);
                if (token) {
                    const decoded: any = jwtDecode(token);
                    const companyId = decoded.companyId;
                    if (companyId) {
                        dispatch(getCompanyById(companyId));
                    }
                }
            } catch (e) {
                console.error("Failed to parse authUser or decode token", e);
            }
        }
    }, [dispatch, authUser]);

    const companyInfo = useMemo(() => {
        if (selectedCompany) {
            return {
                ruc: selectedCompany.ruc || 'N/A',
                nombreComercial: selectedCompany.tradeName || selectedCompany.businessName || 'N/A',
                ambiente: selectedCompany.environmentType === 1 ? 'Pruebas' : 'Producción',
                firmaDigital: selectedCompany.signatures && selectedCompany.signatures.length > 0 ? 'Activa' : 'No configurada',
                logoUrl: selectedCompany.logoUrl || null
            };
        }
        return {
            ruc: '---',
            nombreComercial: '---',
            ambiente: '---',
            firmaDigital: '---',
            logoUrl: null
        };
    }, [selectedCompany]);

    if (
        isAdmin &&
        !isSuperAdmin &&
        companyId &&
        selectedCompany &&
        !isCompanyInformationComplete(selectedCompany)
    ) {
        return <RestrictionMessage type="admin-incomplete-company" navigate={navigate} />;
    }

    if (isAdmin && !isSuperAdmin && !companyId) {
        return <RestrictionMessage type="admin-no-company" navigate={navigate} />;
    }

    if (isUser && !companyId) {
        return <RestrictionMessage type="user-no-company" navigate={navigate} />;
    }

    if (isUser && companyId && !selectedCompany && !isLoadingCompanies) {
        return <RestrictionMessage type="user-no-company" navigate={navigate} />;
    }

    document.title = 'Dashboard | Vuelo';

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Dashboard" pageTitle="Inicio" />

                    {/* Banner de Bienvenida */}
                    <Row className="mb-4">
                        <Col xs={12}>
                            <Card className="overflow-hidden border-0 bg-soft-primary">
                                <CardBody className="py-4 px-4">
                                    {/* align-items-center centrará los bloques horizontalmente en móvil y verticalmente en escritorio */}
                                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 gap-md-0">

                                        {/* 1. Agregamos text-center para móviles y text-md-start para escritorio */}
                                        <div className="flex-grow-1 text-center text-md-start">
                                            <h4 className="fw-semibold text-primary mb-2">¡Bienvenido a Vuelo!</h4>
                                            {/* Quitamos el padding derecho en móvil (pe-0) para que el centrado sea simétrico */}
                                            <p className="text-muted mb-0 pe-0 pe-md-2">
                                                Conecta tus ideas, factura tus ventas y <b>avanza</b> hacia el crecimiento de tu negocio.
                                            </p>
                                        </div>

                                        {/* 2. Los botones se apilarán uno debajo del otro y se centrarán gracias al contenedor padre */}
                                        <div className="flex-shrink-0 d-flex flex-column flex-md-row gap-2">
                                            <Button color="primary" className="btn-label" onClick={() => navigate('/billing')}>
                                                <i className="ri-file-text-line label-icon align-middle fs-16 me-2"></i>
                                                Nueva Factura
                                            </Button>
                                            <Button color="primary" className="btn-label" onClick={() => navigate('/remission-guide')}>
                                                <i className="ri-truck-line label-icon align-middle fs-16 me-2"></i>
                                                Nueva Guía de Remisión
                                            </Button>
                                        </div>

                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Bloque Principal del Dashboard */}
                    <Row className="mt-4 align-items-stretch mb-4">

                        {/* COLUMNA IZQUIERDA: Información de la Empresa */}
                        <Col xl={8} xs={12} className="mb-4 mb-xl-0 d-flex">
                            <Card className="w-100 h-100 border-0 shadow-sm">
                                <CardBody>
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="avatar-sm flex-shrink-0 me-3">
                                            {companyInfo.logoUrl ? (
                                                <img
                                                    src={companyInfo.logoUrl}
                                                    alt="Logo Empresa"
                                                    className="rounded-circle"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <span className="avatar-title bg-soft-primary rounded-circle text-primary">
                                                <i className="ri-building-line fs-20"></i>
                                            </span>
                                            )}
                                        </div>
                                        <div className="flex-grow-1">
                                            <h5 className="card-title mb-1">Información de la Empresa</h5>
                                            <p className="text-muted mb-0">Datos actuales configurados en el sistema</p>
                                        </div>
                                    </div>

                                    {isLoadingCompanies ? (
                                        <div className="text-center py-4"><Spinner color="primary" /></div>
                                    ) : (
                                        <Row className="g-4">
                                            <Col sm={6}>
                                                <div className="p-3 border border-dashed rounded">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div className="avatar-xs me-2">
                                                        <span className="avatar-title rounded-circle bg-light text-primary">
                                                            <i className="ri-bank-card-line"></i>
                                                        </span>
                                                        </div>
                                                        <h6 className="mb-0">RUC</h6>
                                                    </div>
                                                    <p className="fs-15 mb-0 text-dark fw-medium">{companyInfo.ruc}</p>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="p-3 border border-dashed rounded">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div className="avatar-xs me-2">
                                                        <span className="avatar-title rounded-circle bg-light text-primary">
                                                            <i className="ri-store-2-line"></i>
                                                        </span>
                                                        </div>
                                                        <h6 className="mb-0">Nombre Comercial</h6>
                                                    </div>
                                                    <p className="fs-15 mb-0 text-dark fw-medium">{companyInfo.nombreComercial}</p>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="p-3 border border-dashed rounded">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div className="avatar-xs me-2">
                                                        <span className="avatar-title rounded-circle bg-light text-success">
                                                            <i className="ri-global-line"></i>
                                                        </span>
                                                        </div>
                                                        <h6 className="mb-0">Ambiente</h6>
                                                    </div>
                                                    <p className="fs-15 mb-0 text-dark fw-medium">{companyInfo.ambiente}</p>
                                                </div>
                                            </Col>
                                            <Col sm={6}>
                                                <div className="p-3 border border-dashed rounded">
                                                    <div className="d-flex align-items-center mb-2">
                                                        <div className="avatar-xs me-2">
                                                        <span className="avatar-title rounded-circle bg-light text-warning">
                                                            <i className="ri-key-2-line"></i>
                                                        </span>
                                                        </div>
                                                        <h6 className="mb-0">Firma Digital</h6>
                                                    </div>
                                                    <p className="fs-15 mb-0 text-dark fw-medium">{companyInfo.firmaDigital}</p>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* COLUMNA DERECHA: Gestión de Documentos */}
                        <Col xl={4} xs={12} className="d-flex flex-column">
                            {/* "h-100" aquí obliga a la fila interna a ocupar el alto exacto de su columna padre */}
                            <Row className="g-4 h-100">

                                {/* Gestión de Facturas */}
                                <Col xl={12} md={6} className="d-flex">
                                    <Card className="card-animate border-0 shadow-sm bg-primary w-100 h-100" style={{ borderRadius: "15px" }}>
                                        {/* El d-flex flex-column y justify-content-between manda el botón hacia abajo automáticamente */}
                                        <CardBody className="p-4 d-flex flex-column justify-content-between">
                                            <div className="d-flex align-items-start">
                                                <div className="flex-grow-1">
                                                    <h5 className="text-white fw-bold mb-1">Gestión de Facturas</h5>
                                                    <p className="text-white-50 small mb-4 pe-2">
                                                        Accede al historial completo de tus comprobantes electrónicos.
                                                    </p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <div className="avatar-md">
                                                        <div className="avatar-title bg-info rounded-circle fs-1">
                                                            <i className="ri-file-text-line text-white opacity-75"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <Button
                                                    color="light"
                                                    className="btn-label rounded-pill shadow-sm overflow-hidden"
                                                    onClick={() => navigate('/billing-list')}
                                                >
                                                    <i className="ri-file-text-line label-icon align-middle fs-16 me-2"></i>
                                                    Gestionar Facturas
                                                </Button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>

                                {/* Gestión de Guías de Remisión */}
                                <Col xl={12} md={6} className="d-flex">
                                    <Card className="card-animate border-0 shadow-sm bg-info w-100 h-100" style={{ borderRadius: "15px" }}>
                                        <CardBody className="p-4 d-flex flex-column justify-content-between">
                                            <div className="d-flex align-items-start">
                                                <div className="flex-grow-1">
                                                    <h5 className="text-white fw-bold mb-1">Guías de Remisión</h5>
                                                    <p className="text-white-50 small mb-4 pe-2">
                                                        Administra traslados y consulta documentos.
                                                    </p>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <div className="avatar-md">
                                                        <div className="avatar-title bg-soft-light rounded-circle fs-1">
                                                            <i className="ri-truck-line text-white opacity-75"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <Button
                                                    color="light"
                                                    className="btn-label rounded-pill shadow-sm overflow-hidden"
                                                    onClick={() => navigate('/remission-guide-list')}
                                                >
                                                    <i className="ri-truck-line label-icon align-middle fs-16 me-2"></i>
                                                    Gestionar Guías
                                                </Button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>

                            </Row>
                        </Col>

                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default Dashboard;
